import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Play, 
  BarChart3, 
  Copy, 
  Trash2, 
  Eye, 
  EyeOff, 
  AlertCircle,
  Loader2,
  FileText,
  Users,
  Hash,
  Plus,
  Edit,
  X,
  FlaskConical,
  Bot,
  Upload,
  Download,
} from 'lucide-react';
import { AppLayout } from './AppLayout';
import { EvaluationTestSummary } from '../types/evaluation';
import { EvaluationApiService } from '../services/evaluationApi';
import { ExecutionLimitWarningPopup } from './ExecutionLimitWarningPopup';
import { ForceDeleteTestDialog } from './ForceDeleteTestDialog';

export const EvaluationListView = () => {
  const navigate = useNavigate();
  const evaluationApi = EvaluationApiService.getInstance();
  
  type TestOriginFilter = 'all' | 'user' | 'ai-retest';
  
  const [tests, setTests] = useState<EvaluationTestSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [testOriginFilter, setTestOriginFilter] = useState<TestOriginFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [sourceTestNames, setSourceTestNames] = useState<Record<string, string | null>>({});
  const [deletingTestId, setDeletingTestId] = useState<string | null>(null);
  const [cloningTestId, setCloningTestId] = useState<string | null>(null);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [pendingExecutionTestId, setPendingExecutionTestId] = useState<string | null>(null);
  const [executionCount, setExecutionCount] = useState(0);
  const [executionLimit, setExecutionLimit] = useState(0);
  const [showForceDeleteDialog, setShowForceDeleteDialog] = useState(false);
  const [forceDeleteTest, setForceDeleteTest] = useState<{ id: string; name: string; executionCount: number } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    loadTests();
  }, [showDeleted, testOriginFilter]);

  const loadTests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const originParam = testOriginFilter === 'all' ? undefined : testOriginFilter;
      const data = await evaluationApi.listEvaluationTests(showDeleted, originParam);
      setTests(data);
      
      // Lazily fetch source test names for AI-originated tests
      const aiTests = data.filter(t => t.sourceEvaluationTestId);
      const newSourceIds = aiTests
        .map(t => t.sourceEvaluationTestId!)
        .filter(id => !(id in sourceTestNames));
      
      if (newSourceIds.length > 0) {
        const uniqueIds = [...new Set(newSourceIds)];
        const nameResults: Record<string, string | null> = {};
        await Promise.all(
          uniqueIds.map(async (id) => {
            try {
              const test = await evaluationApi.getEvaluationTest(id);
              nameResults[id] = test?.name ?? null;
            } catch {
              nameResults[id] = null;
            }
          })
        );
        setSourceTestNames(prev => ({ ...prev, ...nameResults }));
      }
    } catch (err: any) {
      console.error('Failed to load evaluation tests:', err);
      setError(err.message || 'Failed to load evaluation tests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecute = async (testId: string) => {
    // Find the test to check if it has an execution limit
    const test = tests.find(t => t.id === testId);
    if (!test) return;

    // Check execution limit before navigating
    if (test.executionLimit) {
      try {
        const executions = await evaluationApi.listExecutions(testId);
        const totalCount = executions.length;

        if (totalCount >= test.executionLimit) {
          // Show warning popup
          setPendingExecutionTestId(testId);
          setExecutionCount(totalCount);
          setExecutionLimit(test.executionLimit);
          setShowLimitWarning(true);
          return;
        }
      } catch (err) {
        console.error('Failed to check execution count:', err);
        // Continue with navigation even if check fails
      }
    }

    // Navigate to execution page
    navigate(`/opinions/evaluation/${testId}/run`);
  };

  const handleContinueExecution = () => {
    if (pendingExecutionTestId) {
      navigate(`/opinions/evaluation/${pendingExecutionTestId}/run`);
      setShowLimitWarning(false);
      setPendingExecutionTestId(null);
    }
  };

  const handleCancelExecution = () => {
    setShowLimitWarning(false);
    setPendingExecutionTestId(null);
  };

  const handleAnalytics = (testId: string) => {
    navigate(`/opinions/evaluation/${testId}/analytics`);
  };

  const handleClone = async (testId: string) => {
    setCloningTestId(testId);
    try {
      const newTestId = await evaluationApi.cloneEvaluationTest(testId);
      // Navigate directly to edit the cloned test
      navigate(`/opinions/evaluation/${newTestId}/edit`);
    } catch (err: any) {
      console.error('Failed to clone test:', err);
      alert(err.message || 'Failed to clone test');
    } finally {
      setCloningTestId(null);
    }
  };

  const handleDelete = async (testId: string, testName: string, executionCount: number) => {
    if (executionCount > 0) {
      // Show force delete dialog
      setForceDeleteTest({ id: testId, name: testName, executionCount });
      setShowForceDeleteDialog(true);
      setDeleteError(null);
      return;
    }

    if (!confirm(`Are you sure you want to delete the test "${testName}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingTestId(testId);
    try {
      await evaluationApi.softDeleteEvaluationTest(testId);
      await loadTests();
    } catch (err: any) {
      console.error('Failed to delete test:', err);
      alert(err.message || 'Failed to delete test');
    } finally {
      setDeletingTestId(null);
    }
  };

  const handleForceDelete = async (password: string) => {
    if (!forceDeleteTest) return;

    setDeletingTestId(forceDeleteTest.id);
    setDeleteError(null);
    try {
      await evaluationApi.forceDeleteEvaluationTest(forceDeleteTest.id, password);
      setShowForceDeleteDialog(false);
      setForceDeleteTest(null);
      await loadTests();
    } catch (err: any) {
      console.error('Failed to force delete test:', err);
      setDeleteError(err.message || 'Failed to force delete test');
    } finally {
      setDeletingTestId(null);
    }
  };

  const handleCancelForceDelete = () => {
    setShowForceDeleteDialog(false);
    setForceDeleteTest(null);
    setDeleteError(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleAddNew = () => {
    navigate('/opinions/evaluation/new');
  };

  const handleEdit = (testId: string) => {
    navigate(`/opinions/evaluation/${testId}/edit`);
  };

  const handleExportPrompts = async (testId: string, testName: string) => {
    try {
      const prompts = await evaluationApi.exportPrompts(testId);
      
      // Create download
      const json = JSON.stringify(prompts, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Sanitize filename: lowercase, spaces to hyphens, remove non-alphanumeric except hyphens
      const sanitizedName = testName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      a.download = `${sanitizedName}-prompts.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to export prompts:', err);
      alert(err.message || 'Failed to export prompts');
    }
  };

  return (
    <AppLayout
      pageTitle="Evaluation Tests"
      pageDescription="Systematic comparison of AI provider configurations"
    >
      {/* Page Actions Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          {/* Origin Filter - Segmented Buttons */}
          <div data-flow="origin-filter" className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {([
              { value: 'all' as TestOriginFilter, label: 'All Tests', icon: undefined },
              { value: 'user' as TestOriginFilter, label: 'User Tests', icon: undefined },
              { value: 'ai-retest' as TestOriginFilter, label: 'AI Retests', icon: Bot },
            ]).map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setTestOriginFilter(value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  testOriginFilter === value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {label}
              </button>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">New Test</span>
            </button>
            <button
              onClick={() => navigate('/opinions/evaluation/batch-run')}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm font-medium">Batch Run</span>
            </button>
            <button
              onClick={() => navigate('/opinions/scoring')}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm font-medium">Scoring Validation</span>
            </button>
            <button
              onClick={() => setShowDeleted(!showDeleted)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showDeleted
                  ? 'bg-red-50 text-red-700 hover:bg-red-100'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {showDeleted ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  <span className="text-sm font-medium">Hide Deleted</span>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  <span className="text-sm font-medium">Show Deleted</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div data-component="EvaluationListView" className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div data-flow="loading" className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="text-gray-500">Loading evaluation tests...</p>
            </div>
          </div>
        ) : error ? (
          <div data-flow="error" className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3 text-red-600">
              <AlertCircle className="w-12 h-12" />
              <p className="font-medium">{error}</p>
              <button
                onClick={loadTests}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : tests.length === 0 ? (
          <div data-flow="empty" className="flex flex-col items-center justify-center h-64 text-gray-500">
            <FileText className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium">No evaluation tests found</p>
            <p className="text-sm mt-2">Create your first test to get started</p>
          </div>
        ) : (
          <div data-flow="results" className="grid gap-4">
            {tests.map((test) => (
              <div
                key={test.id}
                className={`bg-white rounded-lg border ${
                  test.isDeleted 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-gray-200 hover:border-gray-300'
                } transition-colors p-6`}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Test Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {test.name}
                      </h3>
                      {test.isDeleted && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                          Deleted
                        </span>
                      )}
                      {(test.testOrigin === 'AIRetest' || test.sourceEvaluationTestId) && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                          <Bot className="w-3 h-3" />
                          AI Retest
                        </span>
                      )}
                      {test.testOrigin === 'Batch' && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-800 text-xs font-medium rounded">
                          <Upload className="w-3 h-3" />
                          Batch Run
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        test.mode === 'Fixed' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {test.mode} Prompt
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {test.purpose}
                    </p>

                    {test.sourceEvaluationTestId && (
                      <div data-flow="source-info" className="mb-3 text-xs">
                        {sourceTestNames[test.sourceEvaluationTestId] === null ? (
                          <span className="text-gray-400">Source test no longer available</span>
                        ) : sourceTestNames[test.sourceEvaluationTestId] ? (
                          <button
                            onClick={() => navigate(`/opinions/evaluation/${test.sourceEvaluationTestId}/analytics`)}
                            className="text-purple-600 hover:text-purple-800 hover:underline transition-colors"
                          >
                            Source: {sourceTestNames[test.sourceEvaluationTestId]}
                          </button>
                        ) : (
                          <span className="text-gray-500">Source: {test.sourceEvaluationTestId}</span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {test.creatorUserId}
                      </span>
                      <span className="flex items-center gap-1">
                        <Hash className="w-4 h-4" />
                        {test.providerCount} provider{test.providerCount !== 1 ? 's' : ''}
                      </span>
                      {test.executionLimit && (
                        <span className="flex items-center gap-1">
                          <BarChart3 className="w-4 h-4" />
                          {test.currentExecutionCount}/{test.executionLimit} executions
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        Created {formatDate(test.createdAt)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleExecute(test.id)}
                      disabled={test.isDeleted}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      title="Execute test"
                    >
                      <Play className="w-4 h-4" />
                      <span className="text-sm font-medium">Execute</span>
                    </button>
                    
                    <button
                      onClick={() => handleAnalytics(test.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="View analytics"
                    >
                      <BarChart3 className="w-4 h-4" />
                      <span className="text-sm font-medium">Analytics</span>
                    </button>
                    
                    <button
                      onClick={() => navigate(`/opinions/evaluation/${test.id}/ai-retest`)}
                      disabled={test.isDeleted}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                      title="AI Evaluation"
                    >
                      <FlaskConical className="w-4 h-4" />
                      <span className="text-sm font-medium">AI Evaluation</span>
                    </button>
                    
                    {!test.isDeleted && (
                      test.currentExecutionCount === 0 ? (
                        <button
                          onClick={() => handleEdit(test.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit test configuration"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleEdit(test.id)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View test details (read-only)"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      )
                    )}
                    
                    <button
                      onClick={() => handleClone(test.id)}
                      disabled={cloningTestId === test.id}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Clone test"
                    >
                      {cloningTestId === test.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleExportPrompts(test.id, test.name)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Export prompts"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(test.id, test.name, test.currentExecutionCount)}
                      disabled={deletingTestId === test.id || test.isDeleted}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        test.isDeleted 
                          ? 'Already deleted' 
                          : test.currentExecutionCount > 0 
                            ? 'Force delete (requires password)' 
                            : 'Delete test'
                      }
                    >
                      {deletingTestId === test.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Execution Limit Warning Popup */}
      {showLimitWarning && (
        <ExecutionLimitWarningPopup
          limit={executionLimit}
          currentCount={executionCount}
          onContinue={handleContinueExecution}
          onCancel={handleCancelExecution}
        />
      )}

      {/* Force Delete Dialog */}
      {showForceDeleteDialog && forceDeleteTest && (
        <ForceDeleteTestDialog
          testName={forceDeleteTest.name}
          executionCount={forceDeleteTest.executionCount}
          onConfirm={handleForceDelete}
          onCancel={handleCancelForceDelete}
          isProcessing={deletingTestId === forceDeleteTest.id}
        />
      )}

      {/* Delete Error Message */}
      {deleteError && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-md z-50">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{deleteError}</p>
            </div>
            <button
              onClick={() => setDeleteError(null)}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};
